import React, { useState } from "react";
import { Server, Loader2, Download, CheckCircle2, Film, Clock, Cpu, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { api, renderAssetUrl, exportAssetUrl, timelineExportUrl, fmtDuration } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import { FilmReviewPanel } from "@/components/finishing/FilmReviewPanel";
import NoProject from "@/components/NoProject";

const EXPORTS = [
  { kind: "master", label: "Master", desc: "720p finished master" },
  { kind: "youtube", label: "YouTube", desc: "1080p H.264" },
  { kind: "h265", label: "H.265 / HEVC", desc: "efficient 4K-ready" },
  { kind: "prores", label: "ProRes 422 HQ", desc: ".mov master-quality" },
  { kind: "vertical", label: "Vertical / Reel", desc: "1080×1920 9:16" },
  { kind: "mobile", label: "Mobile", desc: "480p small file" },
];

export default function Render() {
  const { projectId, system } = useApp();
  const [busy, setBusy] = useState("");
  const [cfg, setCfg] = useState({ codec: "h264", resolution: "1080p", source: "proxy", transitions: true });
  const { data } = usePoll(() => (projectId ? api.renderStatus(projectId) : Promise.resolve(null)), 2000, [projectId]);
  const { data: exp } = usePoll(() => (projectId ? api.exports(projectId) : Promise.resolve(null)), 3000, [projectId]);
  if (!projectId) return <NoProject title="Render needs a project" />;

  const render = data?.render;
  const job = data?.job;
  const running = job && (job.status === "running" || job.status === "queued");
  const pct = data?.total_scenes ? Math.round((data.segments_done / data.total_scenes) * 100) : (job?.progress || 0);
  const complete = render?.status === "complete";
  const exportsDone = {}; (exp?.exports || []).forEach((e) => (exportsDone[e.kind] = e));

  const start = async () => { setBusy("render"); try { await api.startRender(projectId, cfg); toast.success(`Render started · ${cfg.codec} ${cfg.resolution}`); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); } };
  const doExport = async (kind) => { setBusy(kind); try { await api.startExport(projectId, kind); toast.success(`${kind} export queued`); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); } };

  const Sel = ({ label, k, opts }) => (
    <div>
      <label className="metric-label block mb-1">{label}</label>
      <select className="studio-input !py-1.5 text-sm" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} data-testid={`render-${k}`}>
        {opts.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
      </select>
    </div>
  );

  const gpu = system?.gpu, cpu = system?.cpu;

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#212534] flex items-center justify-center"><Server size={22} className="text-[#E6B04E]" /></div>
          <div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Render &amp; Export</h1>
            <p className="text-[#94A3B8] text-sm">Checkpointed scene→chapter→master render · resumes after interruption</p>
          </div>
        </div>
        <button className="btn btn-gold" onClick={start} disabled={busy === "render" || running} data-testid="btn-start-render">
          {busy === "render" || running ? <Loader2 size={16} className="animate-spin" /> : <Server size={16} />} {complete ? "Re-render" : "Render Master"}
        </button>
      </div>

      {/* render settings */}
      <div className="studio-card p-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Sel label="Codec" k="codec" opts={[["h264", "H.264 / AVC"], ["h265", "H.265 / HEVC"]]} />
          <Sel label="Resolution" k="resolution" opts={[["720p", "720p"], ["1080p", "1080p"], ["4k", "4K UHD"]]} />
          <Sel label="Source" k="source" opts={[["proxy", "Proxy (fast)"], ["raw", "Original RAW (4K master)"]]} />
          <div>
            <label className="metric-label block mb-1">Transitions</label>
            <button onClick={() => setCfg({ ...cfg, transitions: !cfg.transitions })} data-testid="render-transitions"
              className={`studio-input !py-1.5 text-sm text-left ${cfg.transitions ? "text-[#E6B04E]" : "text-[#64748B]"}`}>
              {cfg.transitions ? "Crossfades ON" : "Hard cuts only"}
            </button>
          </div>
        </div>
        <p className="text-[#64748B] text-xs mt-3">
          Encoder: {system?.gpu?.available ? `${system.gpu.name} (NVENC)` : "CPU x264/x265 (no GPU in this environment)"} ·
          RAW source renders a true {cfg.resolution.toUpperCase()} master from originals (slower).
        </p>
      </div>

      {/* progress + telemetry */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="studio-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="metric-label">Render Progress</span>
            <span className="font-mono text-sm" style={{ color: complete ? "#10B981" : "#E6B04E" }}>
              {complete ? "COMPLETE" : running ? `${pct}%` : (render ? "idle" : "not started")}
            </span>
          </div>
          <div className="progress-track mb-3"><div className="progress-fill" style={{ width: `${complete ? 100 : pct}%` }} /></div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="font-head text-xl font-bold">{data?.segments_done ?? 0}/{data?.total_scenes ?? 0}</div><div className="metric-label" style={{ fontSize: 9 }}>scenes</div></div>
            <div><div className="font-head text-xl font-bold">{render?.validation?.duration ? fmtDuration(render.validation.duration) : "—"}</div><div className="metric-label" style={{ fontSize: 9 }}>duration</div></div>
            <div><div className="font-head text-xl font-bold">{render?.validation?.resolution || "—"}</div><div className="metric-label" style={{ fontSize: 9 }}>resolution</div></div>
          </div>
        </div>
        <div className="studio-card p-5">
          <span className="metric-label">Encoder</span>
          <div className="space-y-2 mt-2 text-sm">
            <div className="flex items-center gap-2"><Cpu size={14} className="text-[#00F0FF]" /> {gpu?.available ? gpu.name : "CPU (x264 veryfast)"}</div>
            <div className="flex items-center gap-2"><HardDrive size={14} className="text-[#8B5CF6]" /> CPU {cpu?.percent ?? "--"}% · RAM {cpu?.ram_percent ?? "--"}%</div>
            <div className="flex items-center gap-2"><Clock size={14} className="text-[#E6B04E]" /> {gpu?.available ? "HW accel" : "software encode"}</div>
          </div>
        </div>
      </div>

      {/* players */}
      {complete && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="studio-card p-5">
            <div className="flex items-center gap-2 mb-3"><Film size={16} className="text-[#E6B04E]" /><h3 className="font-head font-bold">Master Film</h3>
              <CheckCircle2 size={15} className="text-[#10B981] ml-auto" /></div>
            <video src={renderAssetUrl(projectId, "master")} controls className="w-full rounded-lg bg-black" data-testid="master-player" />
            <div className="metric-label mt-2" style={{ fontSize: 9 }}>validated · {render.validation?.has_audio ? "audio ✓" : "no audio"} · {render.scenes} scenes</div>
          </div>
          {render.storage_highlight && (
            <div className="studio-card p-5">
              <div className="flex items-center gap-2 mb-3"><Film size={16} className="text-[#00F0FF]" /><h3 className="font-head font-bold">Highlight / Trailer</h3></div>
              <video src={renderAssetUrl(projectId, "highlight")} controls className="w-full rounded-lg bg-black" data-testid="highlight-player" />
            </div>
          )}
        </div>
      )}

      <FilmReviewPanel projectId={projectId} hasRender={complete} />

      {/* exports */}
      <div>
        <h2 className="font-head text-xl font-bold mb-3">Export Deliverables</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {EXPORTS.map((e) => {
            const done = exportsDone[e.kind];
            return (
              <div key={e.kind} className="studio-card p-4" data-testid={`export-${e.kind}`}>
                <div className="font-semibold text-sm mb-0.5">{e.label}</div>
                <div className="metric-label mb-3" style={{ fontSize: 9 }}>{e.desc}</div>
                {done ? (
                  <a href={exportAssetUrl(projectId, e.kind)} target="_blank" rel="noreferrer" className="btn btn-ghost !py-1.5 w-full text-xs" data-testid={`download-${e.kind}`}>
                    <Download size={13} /> {done.validation?.resolution || "ready"}
                  </a>
                ) : (
                  <button className="btn btn-gold !py-1.5 w-full text-xs" onClick={() => doExport(e.kind)} disabled={!complete || busy === e.kind} data-testid={`export-btn-${e.kind}`}>
                    {busy === e.kind ? <Loader2 size={13} className="animate-spin" /> : "Export"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {!complete && <p className="text-[#64748B] text-xs mt-3">Render the master first to enable exports.</p>}
      </div>

      {/* Phase 8 — pro editor handoff */}
      <div>
        <h2 className="font-head text-xl font-bold mb-1">Professional Editor Handoff</h2>
        <p className="text-[#94A3B8] text-sm mb-3">Export the edit decision list / timeline referencing your <b>original</b> source files — relink and finish in a pro NLE.</p>
        <div className="flex flex-wrap gap-3">
          <a href={timelineExportUrl(projectId, "edl")} className="btn btn-ghost text-sm" data-testid="export-edl"><Download size={14} /> EDL (CMX3600)</a>
          <a href={timelineExportUrl(projectId, "xml")} className="btn btn-ghost text-sm" data-testid="export-xml"><Download size={14} /> Premiere / Resolve XML</a>
          <a href={timelineExportUrl(projectId, "fcpxml")} className="btn btn-ghost text-sm" data-testid="export-fcpxml"><Download size={14} /> Final Cut FCPXML</a>
          <span className="btn btn-ghost text-sm opacity-50 cursor-not-allowed" title="Requires Avid AAF SDK — use EDL/XML">AAF (unsupported)</span>
        </div>
        <p className="text-[#64748B] text-xs mt-2">EDL &amp; FCP7 XML import into DaVinci Resolve, Adobe Premiere Pro and Avid. Slow-motion is flagged for retime in the NLE.</p>
      </div>
    </div>
  );
}
